<script lang="ts">
  import type ArchieML from '$locales/en/content.json';

  interface Props {
    /* Whether the app is being rendered in an embed */
    embedded?: boolean;
    /* ArchieML story content */
    content: (typeof ArchieML)['story'];
  }

  let { embedded = false, content }: Props = $props();

  import { assets } from '$app/paths';

  import {
    Article,
    Analytics,
    BodyText,
    EndNotes,
    SiteHeadline,
    GraphicBlock,
    InlineAd,
    FeaturePhoto,
    Video,
  } from '@reuters-graphics/graphics-components';
  import LogBlock from './components/dev/LogBlock.svelte';
  import { containerWidth, inlineAdNumber } from '$utils/propValidators';
  import { isReutersDotcom } from '$utils/env';
  import { page } from '$app/state';
  import pkg from '$pkg';
  import SequenceTitle from './components/SquenceTitle.svelte';

  // Import ai2svelte components...
  import AiMap from './ai2svelte/ai-chart.svelte';
  import Sequence1 from './ai2svelte/sequence1.svelte';
  import Sequence2 from './ai2svelte/sequence2.svelte';
  import Sequence3 from './ai2svelte/sequence3.svelte';
  import Sequence4 from './ai2svelte/sequence4.svelte';
  import Sequence5 from './ai2svelte/sequence5.svelte';
  import StreetMap from './ai2svelte/streetMap.svelte';
  import Video2 from './ai2svelte/video2.svelte';
  import Video3 from './ai2svelte/video3.svelte';
  import Video4 from './ai2svelte/video4.svelte';
  import Video4_2 from './ai2svelte/video4_2.svelte';
  import AirbagImage from './ai2svelte/airbagImage.svelte';

  // ...and add them to this object.
  const aiCharts = {
    AiMap,
    Sequence1,
    Sequence2,
    Sequence3,
    Sequence4,
    Sequence5,
    StreetMap,
    Video2,
    Video3,
    Video4,
    Video4_2,
    AirbagImage,
  };
</script>

{#if !embedded && isReutersDotcom(page.url)}
  <Analytics authors={pkg?.reuters?.graphic?.authors || []} />
{/if}

<Article>
  <!--
    This component and others are part of our components library.
    📚 Read the docs: https://reuters-graphics.github.io/graphics-components/
  -->
  <SiteHeadline
    hed={content.hed}
    section={content.section}
    sectionUrl={content.sectionUrl}
    authors={content.authors}
    publishTime={content.publishTime}
    updateTime={content.updateTime}
  />

  <!-- 🔁 Looping through your ArchieML doc blocks... -->
  {#each content.blocks as block}
    <!-- Text block -->
    {#if block.type === 'text'}
      <BodyText text={block.text} />

      <!-- Video Block -->
    {:else if block.type === 'video'}
      <Video
        ariaDescription={block.ariaDescription}
        src={`${assets}/${block.src}`}
        width={block.width}
        notes={block.notes}
        loopVideo={true}
        muteVideo={false}
        soundAutoplay={false}
        showControls={true}
      />

      <!-- Ai2svelte graphic block -->
    {:else if block.type === 'ai-graphic'}
      {#if !aiCharts[block.chart]}
        <LogBlock message={`Unable to find "${block.chart}" in aiCharts`} />
      {:else}
        {@const AiChart = aiCharts[block.chart]}
        <GraphicBlock
          id={block.chart}
          width={containerWidth(block.width)}
          textWidth={containerWidth(block.textWidth)}
          title={block.title}
          description={block.description}
          notes={block.notes}
          ariaDescription={block.altText}
        >
          <AiChart assetsPath={assets || '/'} />
        </GraphicBlock>
      {/if}
    {:else if block.type === 'sequence-title'}
      <SequenceTitle {...block} />
      <!-- Inline ad -->
    {:else if block.type === 'inline-ad'}
      {#if isReutersDotcom(page.url)}
        <InlineAd n={inlineAdNumber(block.n)} />
      {:else}
        <LogBlock level="info" message="An ad will appear here on dotcom" />
      {/if}

      <!-- Warning block -->
    {:else if block.type === 'photo'}
      <FeaturePhoto src="{assets}/{block.src}" altText={block.altText} />
    {:else}
      <LogBlock message={`Unknown block type: "${block.type}"`} />
    {/if}
  {/each}

  <EndNotes notes={content.endNotes} />
</Article>
